// state/unread-messages.ts — FP8 unread-message badge store.
//
// W6 v1.1 loaded the unread count only when the drawer opened ("We don't
// poll"). FP8 surfaces the badge on the hamburger from app mount, so a student
// who has a fresh retention nudge sees the dot without first opening the menu.
//
// The store holds a single integer. It is hydrated:
//   - once at app mount (AuthGate), and
//   - on every drawer open (LeftDrawer), so it reconciles after a mark-as-read.
// Mark-as-read on the inbox screen can also optimistically `decrement()`.

import { create } from 'zustand';
import { messagesApi } from '../api/messages';

interface UnreadState {
  count: number;
  /** True while a refresh is in flight (debounce concurrent fetches). */
  loading: boolean;
  /** Fetch the authoritative unread count from the inbox endpoint. */
  refresh: () => Promise<void>;
  /** Set the count directly (e.g. from a payload that already carries it). */
  set: (n: number) => void;
  /** Optimistic decrement on mark-as-read; never goes below 0. */
  decrement: () => void;
}

export const useUnreadMessages = create<UnreadState>((set, get) => ({
  count: 0,
  loading: false,
  async refresh() {
    if (get().loading) return;
    set({ loading: true });
    try {
      const p = await messagesApi.list();
      set({ count: p.unread_count, loading: false });
    } catch {
      // Network / no-token: leave the prior count, just clear the flag.
      set({ loading: false });
    }
  },
  set(n: number) {
    set({ count: Math.max(0, n) });
  },
  decrement() {
    set({ count: Math.max(0, get().count - 1) });
  },
}));
